import { Box, IconButton, Modal, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { CommentSection } from '../CommentSection';
import { DraggableBottomSheetModal } from '../Draggable/DraggableBottomSheet';
import { IProjectComment } from '../../redux/project/types';
import { IFeedComment, TFeedComment } from '../../redux/activity/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { CommentInput } from '../Comments/CommentsInput';
import { getFeedComments } from '../../redux/activity/actions';
import {
    leaveComment,
    likeComment,
    updateComment,
    deleteComment,
    deleteCommentLike,
} from '../../redux/project/actions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { feedCommentsSelector, likeActionInProgressSelector } from '../../selectors/selectors';

interface IFeedContentDisplayModal {
    open: boolean;
    onClose: VoidFunction;
    title: String;
    component: JSX.Element;
    categoryId: string;
    category: TFeedComment;
    onSendComment?: (text: string, parent?: IProjectComment) => void;
    onLike?: (comment: IProjectComment) => void;
    onEdit?: (comment: IProjectComment, newText: string) => void;
    onDelete?: (comment: IProjectComment) => void;
}

export const FeedContentDisplayModal: React.FC<IFeedContentDisplayModal> = ({
    open,
    onClose,
    title,
    component,
    categoryId,
    category,
    onSendComment,
    onLike,
    onEdit,
    onDelete,
}) => {
    const dispatch = useDispatch();
    const osTheme = useTheme();
    const isMobile = useMediaQuery(osTheme.breakpoints.down('sm'));
    const [showComments, setShowComments] = useState(true);
    const { themeStyle } = useThemeContext();

    // Local state for UI management only
    const [loadingMoreComments, setLoadingMoreComments] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        hasNextPage: false,
        total: 0,
    });

    // Redux selectors for comment operations
    const {
        fetchingComments,
        commentsFromRedux = [],
        pagination: rPagination,
        fetchingMoreReplies,
    } = useSelector(feedCommentsSelector);

    const likingInProgress = useSelector(likeActionInProgressSelector);

    // Load initial comments
    useEffect(() => {
        if (open && categoryId && category) {
            dispatch(
                getFeedComments({
                    category,
                    categoryId,
                    parent_comment: '',
                    page: 1,
                    limit: pagination.limit,
                }),
            );
        }
    }, [open, categoryId, category, dispatch, pagination.limit]);

    // Update local pagination when Redux pagination changes
    useEffect(() => {
        if (rPagination) {
            setPagination({
                page: rPagination.page,
                limit: rPagination.limit,
                hasNextPage: rPagination.hasNextPage,
                total: rPagination.total,
            });
            setLoadingMoreComments(false);
        }
    }, [rPagination]);

    // Clear loading states when comments are updated
    useEffect(() => {
        if (!fetchingComments) {
            setLoadingMoreComments(false);
        }
    }, [fetchingComments, commentsFromRedux]);

    const handleSendComment = (text: string, parent?: IFeedComment) => {
        if (onSendComment) {
            onSendComment(text, parent as any);
            return;
        }

        // Create comment payload based on category
        const commentPayload: any = {
            comment: text,
            commentType: category,
            isReply: !!parent,
            parentComment: parent,
        };

        // Add the specific entity based on category
        if (category === 'project') {
            commentPayload.project = { _id: categoryId };
        } else if (category === 'thougth') {
            commentPayload.thougth = { _id: categoryId };
        } else if (category === 'contest') {
            commentPayload.contest = { _id: categoryId };
        }

        // Add commentOrigin to indicate this comment comes from feed
        commentPayload.commentOrigin = 'feed';
        dispatch(leaveComment(commentPayload));
    };

    const handleLike = (comment: IFeedComment) => {
        if (onLike) {
            onLike(comment as any);
            return;
        }

        // Don't proceed if liking is already in progress
        if (likingInProgress) {
            console.log('Liking in progress');
            return;
        }

        try {
            if (comment.is_user_liked && comment.user_like && comment.user_like.length > 0) {
                // User wants to unlike - use deleteCommentLike
                dispatch(
                    deleteCommentLike({
                        documentId: comment.user_like[0]._id,
                        additionalPayload: comment,
                        cache: `${categoryId}-comment`,
                    }),
                );
            } else {
                // User wants to like - use likeComment
                dispatch(
                    likeComment({
                        commentId: comment._id,
                        like_category: 'comment',
                        like_type: 'like',
                        like_origin: 'details',
                        cache: `${categoryId}-comment`,
                    }),
                );
            }
        } catch (error) {
            console.error('Error handling comment like:', error);
        }
    };

    const handleEdit = (comment: IFeedComment, newText: string) => {
        if (onEdit) {
            onEdit(comment as any, newText);
            return;
        }

        // Use the updateComment action
        dispatch(
            updateComment({
                documentId: comment._id,
                payload: newText,
            }),
        );
    };

    const handleDelete = (comment: IFeedComment) => {
        if (onDelete) {
            onDelete(comment as any);
            return;
        }

        // Use the deleteComment action
        dispatch(
            deleteComment({
                documentId: comment._id,
                additionalPayload: comment._id,
                payload: comment,
            }),
        );
    };

    const handleLoadMoreComments = () => {
        if (pagination.hasNextPage && !loadingMoreComments) {
            setLoadingMoreComments(true);
            dispatch(
                getFeedComments({
                    category,
                    categoryId,
                    parent_comment: '',
                    page: pagination.page + 1,
                    limit: pagination.limit,
                }),
            );
        }
    };

    const handleLoadMoreReplies = (comment: IFeedComment) => {
        /** Get the page details */

        const payload = {
            category,
            categoryId,
            parent_comment: comment._id,
            page: comment.pagination?.nextPage,
            limit: comment.pagination?.limit,
        };

        dispatch(getFeedComments(payload));
    };

    const handleShowComments = () => {
        setShowComments(true);
    };

    const handleCloseComments = () => {
        setShowComments(false);
    };

    const handleMainModalClose = () => {
        setShowComments(false); // Close comments when main modal closes
        setPagination({ page: 1, limit: 10, hasNextPage: false, total: 0 });
        onClose();
    };

    // Convert IFeedComment to IProjectComment for compatibility
    const convertToProjectComments = (feedComments: IFeedComment[]): any[] => {
        const convertSingleComment = (comment: IFeedComment): any => ({
            ...comment,
            user: comment.created_by,
            project: comment.project || '',
            total_likes: comment.total_likes || 0,
            total_anger: comment.total_anger || 0,
            total_love: comment.total_love || 0,
            replies: comment.first_replies?.map(reply => convertSingleComment(reply)) || [],
            __v: 0,
            id: comment._id,
            createdAt: comment.created_at,
            updatedAt: comment.created_at,
            total_replies: comment.total_replies || 0,
        });

        return feedComments.map(comment => convertSingleComment(comment));
    };

    const convertedComments = convertToProjectComments(commentsFromRedux);

    // Create wrapper functions to handle type conversion
    const handleSendCommentWrapper = (text: string, parent?: any) => {
        const feedParent = parent
            ? {
                  ...parent,
                  first_replies: parent.replies || [],
                  total_replies: parent.total_replies || 0,
                  created_by: parent.user,
                  created_at: parent.createdAt,
              }
            : undefined;
        return handleSendComment(text, feedParent);
    };

    const handleLikeWrapper = (comment: any) => {
        const feedComment = {
            ...comment,
            first_replies: comment.replies || [],
            total_replies: comment.total_replies || 0,
            created_by: comment.user,
            created_at: comment.createdAt,
        };
        return handleLike(feedComment);
    };

    const handleEditWrapper = (comment: any, newText: string) => {
        const feedComment = {
            ...comment,
            first_replies: comment.replies || [],
            total_replies: comment.total_replies || 0,
            created_by: comment.user,
            created_at: comment.createdAt,
        };
        return handleEdit(feedComment, newText);
    };

    const handleDeleteWrapper = (comment: any) => {
        const feedComment = {
            ...comment,
            first_replies: comment.replies || [],
            total_replies: comment.total_replies || 0,
            created_by: comment.user,
            created_at: comment.createdAt,
        };
        return handleDelete(feedComment);
    };

    // Comments Bottom Sheet Portal - with proper focus management
    const commentsBottomSheet =
        open && showComments && isMobile
            ? createPortal(
                  <DraggableBottomSheetModal
                      open={showComments}
                      onDismiss={handleCloseComments}
                      customHeight="80%"
                      minHeight={300}
                      maxHeight={window.innerHeight * 0.9}
                      hideCloseIcon={false}
                      style={{ zIndex: 1400 }}>
                      <Box
                          sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              pt: 1,
                              overflow: 'hidden', // Prevent overall scroll
                          }}>
                          <Typography variant="h6" sx={{ px: 2, pb: 2, color: 'white', flexShrink: 0 }}>
                              Comments ({pagination.total})
                          </Typography>

                          {/* Scrollable Comments Area */}
                          <Box
                              sx={{
                                  flex: 1,
                                  px: 2,
                                  overflowY: 'auto',
                                  minHeight: 0, // Important for flex child to scroll
                              }}>
                              {/* Comments Only */}
                              <CommentSection
                                  comments={convertedComments}
                                  onSendComment={handleSendCommentWrapper}
                                  onLike={handleLikeWrapper}
                                  onEdit={handleEditWrapper}
                                  onDelete={handleDeleteWrapper}
                                  hideMainInput={true} // Hide the main input from CommentSection
                                  loadMoreReplies={comment => handleLoadMoreReplies(comment)}
                                  fetchingComments={fetchingComments}
                                  fetchingMoreReplies={fetchingMoreReplies}
                              />

                              {/* Load More Comments Button - only show if top pagination has next page */}
                              {pagination.hasNextPage && (
                                  <Box sx={{ textAlign: 'center', py: 2 }}>
                                      <Button
                                          variant="outlined"
                                          onClick={handleLoadMoreComments}
                                          disabled={loadingMoreComments}
                                          sx={{
                                              color: theme[themeStyle].tag_white,
                                              borderColor: theme[themeStyle].tag_white,
                                          }}>
                                          {loadingMoreComments ? 'Loading...' : 'Load More Comments'}
                                      </Button>
                                  </Box>
                              )}
                          </Box>

                          {/* Fixed Comment Input at Bottom */}
                          <Box
                              sx={{
                                  flexShrink: 0,
                                  px: 2,
                                  py: 1,
                                  borderTop: `1px solid ${theme[themeStyle].tag_white}20`,
                                  bgcolor: theme[themeStyle].accountGray,
                              }}>
                              <CommentInput
                                  onCommentSend={text => handleSendComment(text)}
                                  placeHolder="Add a comment..."
                              />
                          </Box>
                      </Box>
                  </DraggableBottomSheetModal>,
                  document.body,
              )
            : null;

    // Mobile version - full screen
    if (isMobile) {
        return (
            <>
                <Modal
                    open={open}
                    onClose={handleMainModalClose}
                    sx={{ zIndex: 1300 }} // Ensure consistent z-index
                    disableEnforceFocus={showComments} // Disable focus trapping when comments are open
                    disableAutoFocus={showComments} // Disable auto focus when comments are open
                >
                    <Box
                        sx={{
                            width: '100vw',
                            height: '100vh',
                            bgcolor: theme[themeStyle].secondaryBg,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                        }}>
                        {/* Header */}
                        <Box
                            sx={{
                                width: '100%',
                                height: 56,
                                bgcolor: theme[themeStyle].deep_grey,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                            }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ color: theme[themeStyle].tag_white, fontWeight: 600 }}>
                                {title}
                            </Typography>
                            <IconButton
                                onClick={handleMainModalClose}
                                sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
                                <CloseIcon stroke={theme[themeStyle].tag_white} />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            {component}

                            {/* Comments Button */}
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'primary.main',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                    onClick={handleShowComments}>
                                    View {pagination.total} comments
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Modal>

                {/* Render comments bottom sheet via portal with higher z-index than main modal */}
                {commentsBottomSheet}
            </>
        );
    }

    // Desktop version - with fixed input and static height
    return (
        <Modal open={open} onClose={handleMainModalClose}>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    height: '80vh', // Static height
                    mx: 'auto',
                    my: 4,
                    bgcolor: theme[themeStyle].secondaryBg,
                    borderRadius: 2,
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}>
                {/* Dark Top Layer */}
                <Box
                    sx={{
                        width: '100%',
                        height: 60,
                        bgcolor: theme[themeStyle].deep_grey,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        position: 'relative',
                        flexShrink: 0,
                    }}>
                    <Typography variant="subtitle1" sx={{ color: theme[themeStyle].tag_white, fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <IconButton
                        onClick={handleMainModalClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
                        <CloseIcon stroke={theme[themeStyle].tag_white} />
                    </IconButton>
                </Box>

                {/* Scrollable Content + Comments Area */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        p: 2,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    {/* Card Component */}
                    <Box sx={{ mb: 3 }}>{component}</Box>

                    {/* Comments Section */}
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                mb: 2,
                            }}>
                            Comments ({pagination.total})
                        </Typography>

                        <CommentSection
                            comments={convertedComments}
                            onSendComment={handleSendCommentWrapper}
                            onLike={handleLikeWrapper}
                            onEdit={handleEditWrapper}
                            onDelete={handleDeleteWrapper}
                            hideMainInput={true} // Hide the main input from CommentSection
                            loadMoreReplies={comment => handleLoadMoreReplies(comment)}
                            fetchingComments={fetchingComments}
                            fetchingMoreReplies={fetchingMoreReplies}
                        />

                        {/* Load More Comments Button - only show if top pagination has next page */}
                        {pagination.hasNextPage && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleLoadMoreComments}
                                    disabled={loadingMoreComments}
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        borderColor: theme[themeStyle].tag_white,
                                    }}>
                                    {loadingMoreComments ? 'Loading...' : 'Load More Comments'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Fixed Comment Input at Bottom */}
                <Box
                    sx={{
                        flexShrink: 0,
                        p: 2,
                        borderTop: `1px solid ${theme[themeStyle].tag_white}20`,
                    }}>
                    <CommentInput onCommentSend={text => handleSendComment(text)} placeHolder="Add a comment..." />
                </Box>
            </Box>
        </Modal>
    );
};
